// Adapted from eval/dashboard/internal_web/src/components/session/TurnDifferentialPanel.tsx (ATM-57): Known so far and All ranked tabs. Keep in step until ATM-60 shares it.
/**
 * Jiva's graph differential after one answer, beside the exchange that holds it — the eval
 * dashboard's `TurnDifferentialPanel`, on a live session's rows.
 *
 * Three views of the same moment: the **Differential** as a slope chart from the previous
 * stored belief (`SlopeChart`), what Jiva had **Known so far** about the patient, and **All
 * ranked** — the whole stored ranking, since the chart shows only its top ten. All three are
 * read off what the backend recorded for that answer; nothing is interpolated.
 *
 * **A fixed height, and every line always present.** The panel sits centred beside the
 * transcript, so any change in its height re-centred it and slid the stepper out from under
 * the reader's cursor between two steps. So one line per disease, the comparison and the
 * footer each held to one line, a gap state that fills the same frame, and the two list views
 * scroll inside that frame rather than grow it.
 */

import { useId, useState } from "react";

import type { DiseaseSnapshot } from "../../api/types";
import { cn } from "../../lib/cn";
import { count, pctBounded } from "../../lib/format";
import { relationWords } from "../../lib/relations";
import { CardPanel } from "../ui/Card";
import NullNote from "../ui/NullNote";
import { tabId, tabPanelId } from "../ui/tabIds";
import Tabs, { type TabItem } from "../ui/Tabs";
import { BeliefGap, CompareNote, PanelHeader, type StepProps } from "./DifferentialParts";
import type { KnownFace, KnownRow } from "./knownSoFar";
import { hasEntries, type ExchangeBelief } from "./liveBeliefs";
import SlopeChart, { DEPTH } from "./SlopeChart";

type View = "differential" | "known" | "ranked";

const VIEWS: readonly TabItem<View>[] = [
  { id: "differential", label: "Differential" },
  { id: "known", label: "Known so far" },
  { id: "ranked", label: "All ranked" },
];

type Props = StepProps & {
  belief: ExchangeBelief;
  /** What Jiva knew about the patient after the same exchange. */
  known: readonly KnownRow[];
  /** Whether the session is still running, which changes what an empty panel means. */
  active: boolean;
  /** The newest answer, when its belief is still being stored; said in the footer. */
  pendingAnswer: number | null;
  /** Opens on "Known so far" when the session stored no belief to chart. */
  noBeliefs?: boolean;
};

/** How much of the ranking the chart shows. */
function truncationLine(entries: readonly DiseaseSnapshot[]): string {
  return entries.length <= DEPTH
    ? ""
    : `${count(DEPTH)} of ${count(entries.length)} ranked diseases shown`;
}

/** One value's face: blue when this answer added it, dotted when it mapped it again. */
function valueFace(face: KnownFace): string {
  return cn(
    "inline-block max-w-full rounded-[10px] border px-1.5 text-[12px] leading-[18px]",
    face === "new"
      ? "border-comment-edge bg-comment-bg text-comment"
      : face === "again"
        ? "border-dotted border-ink-faint bg-surface text-ink-3"
        : "border-hairline-2 bg-surface text-ink-3",
  );
}

const FACE_TITLE: Record<KnownFace, string | undefined> = {
  new: "Added at this answer — not known before it.",
  again: "Mapped again from this answer — already known before it.",
  held: undefined,
};

/** The confirmed values Jiva held after this answer, by graph relation. */
function KnownView({ known }: { known: readonly KnownRow[] }) {
  if (known.length === 0) {
    return <NullNote>Nothing confirmed about the patient yet at this answer.</NullNote>;
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-center gap-3 text-[11px] leading-4 text-ink-faint">
        <span className="flex items-center gap-1.5">
          <span aria-hidden className={cn(valueFace("new"), "h-3 w-4 px-0")} />
          new at this answer
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden className={cn(valueFace("again"), "h-3 w-4 px-0")} />
          mapped again
        </span>
      </p>
      <dl className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 gap-y-1">
        {known.map((row) => (
          <div key={row.relation} className="contents">
            <dt className="truncate text-[11px] leading-[20px] text-ink-faint" title={row.relation}>
              {relationWords(row.relation)}
            </dt>
            <dd className="flex min-w-0 flex-wrap items-start gap-1">
              {row.values.map(({ value, face }) => (
                <span key={value} className={valueFace(face)} title={FACE_TITLE[face]}>
                  {value}
                  {face === "new" ? <span className="sr-only"> (new at this answer)</span> : null}
                  {face === "again" ? <span className="sr-only"> (mapped again)</span> : null}
                </span>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The whole stored ranking, one line per disease. */
function RankedView({ entries }: { entries: readonly DiseaseSnapshot[] }) {
  return (
    <table className="w-full table-fixed border-collapse">
      <caption className="sr-only">Every disease in this answer&rsquo;s stored ranking</caption>
      <thead>
        <tr className="text-[11px] leading-4 text-ink-faint">
          <th scope="col" className="w-6 pb-1 text-left font-normal">
            #
          </th>
          <th scope="col" className="pb-1 text-left font-normal">
            Disease
          </th>
          <th scope="col" className="w-16 pb-1 text-right font-normal">
            Likelihood
          </th>
          <th scope="col" className="w-14 pb-1 text-right font-normal">
            Conf.
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={`${entry.rank}-${entry.disease_name}`} className="hairline">
            <td className="py-1.5 text-[11px] tabular-nums text-ink-faint">{entry.rank}</td>
            <td className="truncate py-1.5 text-[13px] text-ink" title={entry.disease_name}>
              {entry.disease_name}
            </td>
            <td className="py-1.5 text-right text-[13px] tabular-nums text-ink">
              {pctBounded(entry.likelihood)}
            </td>
            <td className="py-1.5 text-right text-[12px] tabular-nums text-ink-3">
              {pctBounded(entry.confidence)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Jiva's belief after one answer, what that answer moved, and what Jiva knew by then. */
export default function TurnDifferentialPanel({
  belief,
  known,
  active,
  pendingAnswer,
  noBeliefs = false,
  ...steps
}: Props) {
  const [view, setView] = useState<View>(noBeliefs ? "known" : "differential");
  const tabsId = useId();
  const entries = belief.differential ?? [];
  const ranked = hasEntries(belief);

  return (
    <CardPanel className="flex h-[31.5rem] flex-col gap-3 px-5 pb-4 pt-4">
      <PanelHeader
        belief={belief}
        active={active}
        heading={
          <Tabs id={tabsId} label="Panel view" items={VIEWS} value={view} onSelect={setView} />
        }
        {...steps}
      />
      <div
        className="flex min-h-0 flex-1 flex-col gap-3"
        role="tabpanel"
        id={tabPanelId(tabsId)}
        aria-labelledby={tabId(tabsId, view)}
      >
        {view === "known" ? (
          belief.answerNumber === null ? (
            <BeliefGap belief={belief} active={active} />
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <KnownView known={known} />
            </div>
          )
        ) : !ranked ? (
          <BeliefGap belief={belief} active={active} />
        ) : view === "ranked" ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <RankedView entries={entries} />
          </div>
        ) : (
          <>
            <CompareNote belief={belief} className="h-5 shrink-0" />
            <div className="min-h-0 flex-1">
              <SlopeChart ranking={entries} previous={belief.previous} />
            </div>
          </>
        )}
      </div>
      {/* Always there, empty when there is nothing to say, so no step is a line shorter. */}
      <p className="flex h-5 shrink-0 items-center gap-3 text-caption text-ink-faint">
        <span className="truncate">
          {view === "differential" && ranked ? truncationLine(entries) : ""}
        </span>
        {pendingAnswer !== null ? (
          <span className="ml-auto shrink-0 text-ink-3" role="status">
            Updating after the newest answer…
          </span>
        ) : null}
      </p>
    </CardPanel>
  );
}
